import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import { getTimelapseById, getTimelapses } from "./api"
import { allowUpdate } from "../../app/store"

/**
 *  State management for Timelapse videos of the AllSky cam
 */
const initialState = {
  byId: {}, // A map of timelapses by id
  matchesByCtx: {
    // root context powering the timelapse list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // timelapse ids
      page: {}, // Pagination information
    },
  },
}

/**
 * Thunk to perform async logic to fetch an Timelapse by its ID
 * @type {AsyncThunk<*, void, {}>}
 */
export const fetchTimelapseById = createAsyncThunk(
  "timelapse/fetch",
  async (id) => {
    return await getTimelapseById(id)
  }
)

/**
 * Thunk to perform async logic to fetch the timelapses matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchTimelapses = createAsyncThunk(
  "timelapses/fetch",
  /**
   *
   * @param filter the filters to apply to the search
   * @param page Pagination information
   * @param context The context from where the query is being made.
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getTimelapses({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Timelapse reducer
 */
export const timelapseSlice = createSlice({
  name: "timelapses",
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(fetchTimelapses.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchTimelapses.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"

          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.matchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((act) => {
              // Add the timelapse to state by id (this can include ids not retrieved in this call)
              state.byId[act.id] = {
                ...act,
                _at: action.payload.at,
              }
              // add the ids that match from this query
              if (state.matchesByCtx[context].ids.indexOf(act.id) === -1) {
                state.matchesByCtx[context].ids.push(act.id)
              }
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchTimelapses.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(fetchTimelapseById.pending, (state, action) => {
        if (!state.byId[action.meta.arg]) {
          state.byId[action.meta.arg] = {}
        }
        state.byId[action.meta.arg]._loadStatus = "loading"
      })
      .addCase(fetchTimelapseById.fulfilled, (state, action) => {
        if (action?.payload?.id) {
          if (
            allowUpdate(
              state.byId[action.payload.id]
                ? state.byId[action.payload.id]._at
                : undefined,
              action.payload._at
            )
          ) {
            state.byId[action.payload.id] = {
              ...action.payload,
              _at: action.payload._at,
            }
          }

          state.byId[action.payload.id]._loadStatus = "idle"
        }
      })
      .addCase(fetchTimelapseById.rejected, (state, action) => {
        state.byId[action.meta.arg]._loadStatus = "rejected"
        state.byId[action.meta.arg]._errorMessage += action.payload
      })
  },
})

// Selector for all timelapses
export const selectTimelapsesById = (state) => state.timelapses.byId

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectTimelapsesByContext(state, `rso/${id}`))
 */
export const selectTimelapsesByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.timelapses.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`timelapses, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectTimelapsesByIds(state, ["1", "2"]))
 */
export const selectTimelapsesByIds = createSelector(
  [
    // first input - extract value from `state`
    selectTimelapsesById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)
export default timelapseSlice.reducer
